// The console's products, in Firebase's groups and tab order, and their icons (1.6px strokes).
import type { ReactNode } from "react";

const svg = (d: ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
export const ICONS: Record<string, ReactNode> = {
  overview: svg(<><path d="M3 11l9-7 9 7" /><path d="M5 10v10h14V10" /></>),
  database: svg(<><ellipse cx="12" cy="5.5" rx="7.5" ry="2.5" /><path d="M4.5 5.5v13c0 1.4 3.4 2.5 7.5 2.5s7.5-1.1 7.5-2.5v-13" /><path d="M4.5 12c0 1.4 3.4 2.5 7.5 2.5s7.5-1.1 7.5-2.5" /></>),
  auth: svg(<><circle cx="12" cy="8" r="3.5" /><path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" /></>),
  storage: svg(<><path d="M3.5 7.5l2-3.5h13l2 3.5" /><rect x="3.5" y="7.5" width="17" height="12" rx="1.5" /><path d="M9.5 12h5" /></>),
  realtime: svg(<><path d="M3 12h3.5l2.5-6 4 12 2.5-6H21" /></>),
  mail: svg(<><rect x="3" y="5.5" width="18" height="13" rx="2" /><path d="M3.5 7l8.5 6 8.5-6" /></>),
  push: svg(<><path d="M6 16V11a6 6 0 1112 0v5l1.5 2h-15z" /><path d="M10 20.5a2 2 0 004 0" /></>),
  usage: svg(<><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>),
  activity: svg(<><path d="M4 6h16M4 12h10M4 18h13" /><circle cx="19" cy="12" r="1.5" fill="currentColor" /></>),
  story: svg(<><path d="M12 3l2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4z" /></>),
  migrate: svg(<><path d="M4 7h12l-3-3M20 17H8l3 3" /></>),
  settings: svg(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.6 1.6 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.6 1.6 0 00-1.8-.3 1.6 1.6 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.6 1.6 0 00-1-1.5 1.6 1.6 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.6 1.6 0 00.3-1.8 1.6 1.6 0 00-1.5-1H3a2 2 0 110-4h.1a1.6 1.6 0 001.5-1 1.6 1.6 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.6 1.6 0 001.8.3H9a1.6 1.6 0 001-1.5V3a2 2 0 114 0v.1a1.6 1.6 0 001 1.5 1.6 1.6 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.6 1.6 0 00-.3 1.8V9a1.6 1.6 0 001.5 1H21a2 2 0 110 4h-.1a1.6 1.6 0 00-1.5 1z" /></>),
  projects: svg(<><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" /></>),
  health: svg(<><path d="M20.5 8.5c0 5-8.5 11-8.5 11s-8.5-6-8.5-11a4.5 4.5 0 018.5-2 4.5 4.5 0 018.5 2z" /></>),
  templates: svg(<><rect x="3.5" y="3.5" width="17" height="17" rx="2" /><path d="M3.5 9h17M9 9v11.5" /></>),
  search: svg(<><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4-4" /></>),
  menu: svg(<><path d="M4 7h16M4 12h16M4 17h16" /></>),
  close: svg(<><path d="M6 6l12 12M18 6L6 18" /></>),
  caret: svg(<><path d="M7 10l5 5 5-5" /></>),
  plus: svg(<><path d="M12 5v14M5 12h14" /></>),
  docs: svg(<><path d="M6 3.5h8.5L19 8v12.5H6z" /><path d="M14 3.5V8h5" /></>),
  back: svg(<><path d="M14 6l-6 6 6 6" /></>),
};

export interface Product {
  id: string;
  label: string;
  group: "top" | "build" | "engage" | "monitor" | "hidden";
  tabs: { id: string; label: string }[];
}

export const PRODUCTS: Product[] = [
  { id: "overview", label: "Project Overview", group: "top", tabs: [] },
  { id: "database", label: "Database", group: "build", tabs: [{ id: "data", label: "Data" }, { id: "rules", label: "Rules" }, { id: "indexes", label: "Indexes" }, { id: "schema", label: "Schema" }, { id: "usage", label: "Usage" }] },
  { id: "auth", label: "Authentication", group: "build", tabs: [{ id: "users", label: "Users" }, { id: "providers", label: "Sign-in method" }, { id: "templates", label: "Templates" }, { id: "settings", label: "Settings" }, { id: "usage", label: "Usage" }] },
  { id: "storage", label: "Storage", group: "build", tabs: [{ id: "files", label: "Files" }, { id: "rules", label: "Rules" }, { id: "usage", label: "Usage" }] },
  { id: "realtime", label: "Realtime", group: "build", tabs: [{ id: "live", label: "Live" }, { id: "usage", label: "Usage" }] },
  { id: "mail", label: "Mail", group: "engage", tabs: [{ id: "overview", label: "Overview" }, { id: "domains", label: "Domains" }, { id: "keys", label: "API keys" }, { id: "logs", label: "Logs" }, { id: "templates", label: "Templates" }] },
  { id: "push", label: "Push", group: "engage", tabs: [{ id: "overview", label: "Overview" }, { id: "apps", label: "Apps" }, { id: "keys", label: "API keys" }, { id: "test", label: "Test send" }, { id: "logs", label: "Logs" }] },
  { id: "usage", label: "Usage", group: "monitor", tabs: [] },
  { id: "activity", label: "Activity", group: "monitor", tabs: [] },
  { id: "story", label: "Story", group: "monitor", tabs: [] },
  { id: "migrate", label: "Migrate from Firebase", group: "hidden", tabs: [] },
  { id: "settings", label: "Project settings", group: "hidden", tabs: [{ id: "general", label: "General" }, { id: "keys", label: "Service keys" }, { id: "integrations", label: "Integrations" }, { id: "members", label: "Members" }] },
];
export const productById = (id: string) => PRODUCTS.find((p) => p.id === id);

export const ADMIN_PAGES = [
  { id: "projects", label: "Projects", icon: "projects" },
  { id: "mail", label: "Mail", icon: "mail" },
  { id: "push", label: "Push (Realm 8)", icon: "push" },
  { id: "health", label: "Collector health", icon: "health" },
];

export const SERIES = ["var(--s1)", "var(--s2)", "var(--s3)", "var(--s4)", "var(--s5)", "var(--s6)", "var(--s7)"];
/** A project's colour: fixed by creation order (oldest first), never by rank. */
export function projectColor(id: string, all: { id: string; createdAt?: unknown }[]): string {
  const byAge = [...all].sort((a, b) => ts(a.createdAt) - ts(b.createdAt));
  const i = byAge.findIndex((p) => p.id === id);
  return i < 0 ? "var(--faint)" : i < SERIES.length ? SERIES[i]! : "var(--faint)";
}
const ts = (v: unknown): number =>
  typeof v === "number" ? v
  : v instanceof Date ? v.getTime()
  : typeof v === "object" && v && "toMillis" in v ? (v as { toMillis(): number }).toMillis()
  : typeof v === "object" && v && "$t" in v ? Number((v as { $t: number }).$t)
  : 0;
export const millis = ts;
